import type { GridSelection, Item } from "@glideapps/glide-data-grid";
import { aiScriptSpreadsheetSheetKey } from "@/payroll/ai-script/query";
import type { IAiSpreadsheetSheetResponse } from "@/payroll/ai-script/types";
import type { AdaptedSpreadsheetSheet } from "./adapter";
import type { CellEditHandlers } from "./cell-edit-handlers";
import { buildFormulaFillEdits, type FormulaFillRequest } from "./formula-fill";
import type {
  ManualRowEditBuffer,
  ViewerEditContext,
} from "./manual-row-edit-buffer";
import { buildBulkClearPayload, buildBulkPastePayload } from "./paste-utils";
import type { useWorkbookClipboard } from "./use-workbook-clipboard";
import { buildHistoryCellEdits } from "./viewer-helpers";
import { buildPasteEntry } from "./workbook-history-ops";

interface CreateBulkEditHandlersParams extends ViewerEditContext {
  adaptedSheet: AdaptedSpreadsheetSheet | null;
  canMutateActiveSheet: boolean;
  hasActiveRegionView: boolean;
  maxDisplayRow: number;
  maxContentCol: number;
  consumePasteMetadata: ReturnType<
    typeof useWorkbookClipboard
  >["consumePasteMetadata"];
  buffer: Pick<ManualRowEditBuffer, "bufferOptimisticPasteEdits">;
  cellEdits: Pick<CellEditHandlers, "commitRawInput">;
}

export const createBulkEditHandlers = ({
  businessId,
  runId,
  queryClient,
  history,
  mutations: { bulkPasteAsync },
  activeSheet,
  recordWorkbookVersion,
  handleMutationError,
  adaptedSheet,
  canMutateActiveSheet,
  hasActiveRegionView,
  maxDisplayRow,
  maxContentCol,
  consumePasteMetadata,
  buffer: { bufferOptimisticPasteEdits },
  cellEdits: { commitRawInput },
}: CreateBulkEditHandlersParams) => {
  const handlePaste = (
    target: Item,
    values: readonly (readonly string[])[],
  ) => {
    const pasteMetadata = consumePasteMetadata();
    if (!adaptedSheet || !canMutateActiveSheet || !activeSheet) return true;

    const pasteValues = pasteMetadata.externalValues ?? values;

    const startRow = target[1] + 1;
    const startCol = target[0] + 1;
    // Region view: trim paste to visible rows; appended rows past the view have no saved position.
    const pastedValues = hasActiveRegionView
      ? pasteValues.slice(0, Math.max(0, maxDisplayRow - target[1]))
      : pasteValues;
    if (pastedValues.length === 0) return false;

    const payload = buildBulkPastePayload({
      values: pastedValues,
      startRow,
      startCol,
      maxContentRow: maxDisplayRow,
      getCellRecord: adaptedSheet.getCellRecord,
      cellTypes: pasteMetadata.cellTypes,
      copiedRange: pasteMetadata.copiedRange,
    });
    if (!payload) return false;

    const persistedPayload = {
      ...payload,
      edits: bufferOptimisticPasteEdits(payload.edits),
    };
    if (
      persistedPayload.edits.length === 0 &&
      persistedPayload.rows.length === 0
    ) {
      return false;
    }

    const sheetId = activeSheet.id;
    const sheetData = queryClient.getQueryData<IAiSpreadsheetSheetResponse>(
      aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    );
    const cellEdits = buildHistoryCellEdits(sheetData, persistedPayload.edits);

    void bulkPasteAsync({ runId, sheetId, ...persistedPayload })
      .then((response) => {
        recordWorkbookVersion(response.version);
        history.push(
          buildPasteEntry({
            sheetId,
            cellEdits,
            createdRowIds: response.manual_row_ids ?? [],
          }),
        );
      })
      .catch((mutationError: unknown) =>
        handleMutationError("Failed to paste cells", mutationError),
      );
    return false;
  };

  const commitFormulaFill = (
    { patternSource, fillDestination }: FormulaFillRequest,
    label: string,
  ): void => {
    if (!adaptedSheet || !canMutateActiveSheet || !activeSheet) return;

    const fillEdits = buildFormulaFillEdits({
      patternSource,
      fillDestination,
      getCellRecord: adaptedSheet.getCellRecord,
    });
    if (fillEdits.length === 0) return;

    const persistedEdits = bufferOptimisticPasteEdits(fillEdits);
    if (persistedEdits.length === 0) return;

    const sheetId = activeSheet.id;
    const sheetData = queryClient.getQueryData<IAiSpreadsheetSheetResponse>(
      aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    );
    const cellEdits = buildHistoryCellEdits(sheetData, persistedEdits);

    void bulkPasteAsync({ runId, sheetId, edits: persistedEdits, rows: [] })
      .then((response) => {
        recordWorkbookVersion(response.version);
        history.push(buildPasteEntry({ sheetId, cellEdits, label }));
      })
      .catch((mutationError: unknown) =>
        handleMutationError("Failed to fill formulas", mutationError),
      );
  };

  // Returning false routes multi-cell clears through one bulk request, not Glide's per-cell clears.
  const handleDelete = (selection: GridSelection): boolean => {
    if (!adaptedSheet || !canMutateActiveSheet || !activeSheet) return true;

    const payload = buildBulkClearPayload({
      selection,
      maxContentRow: maxDisplayRow,
      maxContentCol,
      getCellRecord: adaptedSheet.getCellRecord,
    });
    if (!payload) return false;

    const persistedEdits = bufferOptimisticPasteEdits(payload.edits);

    if (persistedEdits.length === 0) return false;
    if (persistedEdits.length === 1) {
      commitRawInput(persistedEdits[0].cell_id, persistedEdits[0].raw_input);
      return false;
    }

    const sheetId = activeSheet.id;
    const sheetData = queryClient.getQueryData<IAiSpreadsheetSheetResponse>(
      aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    );
    const cellEdits = buildHistoryCellEdits(sheetData, persistedEdits);

    void bulkPasteAsync({ runId, sheetId, edits: persistedEdits, rows: [] })
      .then((response) => {
        recordWorkbookVersion(response.version);
        history.push(buildPasteEntry({ sheetId, cellEdits }));
      })
      .catch((mutationError: unknown) =>
        handleMutationError("Failed to clear cells", mutationError),
      );
    return false;
  };

  return { handlePaste, commitFormulaFill, handleDelete };
};
